import type { CopySourceCourse, TeacherCourseAPIResponse } from 'app/types/modules';
import { getTeacherCourses } from 'core/crud/teacherCourses.crud';
import { useEffect, useState } from 'react';

type UseCopySourceCoursesParams = {
  isOpen: boolean;
  /** Programa actual: el backend excluye sus cursos vía `exclude_program`. */
  programGuid: string;
};

type UseCopySourceCoursesReturn = {
  courses: CopySourceCourse[];
  hasError: boolean;
  isLoading: boolean;
};

/**
 * Cursos origen del paso 1 del wizard "Copiar módulo" (TAN-10192): programas
 * licenciados y cursos de aula del profesor, ya sin los del programa actual.
 *
 * Solo pide datos con el diálogo abierto: el wizard se monta bajo demanda, pero
 * el flag evita la petición si el consumidor lo dejara montado.
 */
function useCopySourceCourses({ isOpen, programGuid }: UseCopySourceCoursesParams): UseCopySourceCoursesReturn {
  const [courses, setCourses] = useState<CopySourceCourse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen || !programGuid) {
      setCourses([]);
      setHasError(false);
      setIsLoading(false);
      return undefined;
    }

    // Evita escribir estado obsoleto si el diálogo se cierra antes de resolver.
    let cancelled = false;
    setIsLoading(true);
    setHasError(false);

    async function loadCourses(): Promise<void> {
      try {
        const response = await getTeacherCourses({ excludeProgramGuid: programGuid });
        if (cancelled) return;

        // El interceptor global de axios resuelve los errores HTTP en vez de
        // rechazarlos (`{ error: ... }`), así que el fallo se detecta por la
        // ausencia de la lista, no solo por el `catch`.
        const data = response?.data?.data;
        if (!Array.isArray(data)) {
          setCourses([]);
          setHasError(true);
          return;
        }

        setCourses(data.map(toCopySourceCourse));
      } catch {
        if (cancelled) return;
        setCourses([]);
        setHasError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadCourses();

    return () => {
      cancelled = true;
    };
  }, [isOpen, programGuid]);

  return { courses, hasError, isLoading };
}

/**
 * El nivel educativo de la UI es la composición de curso académico y etapa
 * ("4º" + "Primaria"): la API los devuelve por separado. Si falta una de las
 * dos partes se compone con la que haya; sin ninguna queda `null` y el grupo
 * cae en la etiqueta "sin agrupar".
 */
function toLevelTitle(course: TeacherCourseAPIResponse): string | null {
  const level = [course?.education_year_name, course?.education_level_name].filter(Boolean).join(' ');
  return level || null;
}

export function toCopySourceCourse(course: TeacherCourseAPIResponse): CopySourceCourse {
  return {
    education_discipline_guid: course?.education_discipline_guid ?? null,
    education_level_guid: course?.education_level_guid ?? null,
    education_year_guid: course?.education_year_guid ?? null,
    education_year_order: course?.education_year_order ?? null,
    guid: course?.guid,
    level_title: toLevelTitle(course),
    name: course?.name ?? '',
    picture: course?.program_cover ?? undefined,
    school_group_guid: course?.school_group_guid ?? null,
    school_group_name: course?.school_group_name ?? null,
    school_guid: course?.school_guid ?? null,
    subject_name: course?.education_discipline_name ?? null,
  };
}

export default useCopySourceCourses;
